import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { withStyles } from '@material-ui/core/styles';
import { emphasize, fade } from '@material-ui/core/styles/colorManipulator';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';

import { Line, Bar, Pie, Doughnut, Polar } from 'react-chartjs-2';

import { http, api } from '../common/common';
import ViewStack from './ViewStack';

const styles = (theme) => {
    return {
        paper: {
            backgroundColor: theme.palette.primary.main,
            width: '100%',
            height: '100%',

        },
        progress: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            width: 60,
            height: 60,
            margin: 'auto',
            color: theme.palette.primary.light,
        },
        errorLabel: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            width: 'auto',
            height: 40,
            margin: 'auto',
            color: theme.palette.primary.displayText,
        },
        chartContainer: {
            position: 'absolute',
            top: 10,
            bottom: 10,
            left: '10%',
            right: '10%',
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: 600,
            overflow: 'visible',
        },
        chart: {
            overflow: 'visible',
        },
    };
}

const Mode = Object.freeze({
    progress: 0,
    done: 1,
    error: 2,
});

class GraphPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            type: 'line',
            data: null,
            xAxisLabel: '',
            yAxisLabel: '',
            mode: Mode.error,
        }

        this._cancelToken = http.CancelToken.source();
        this._pending = false;
    }

    shouldComponentUpdate = (nextProps, nextState) => {
        const nextView = nextProps.viewStack.peek();

        if (!nextView.layer) {
            return false;
        }

        const nextUid = nextView.uid;
        const [nextLayerType, nextLayerIndex] = nextView.layerSelection;

        if (this._uid !== nextUid ||
            this._layerType !== nextLayerType ||
            this._layerIndex !== nextLayerIndex) {

            this._pending = true;
            this._uid = '';

            return true;
        }

        return (nextProps.open !== this.props.open || (
            nextProps.open && this.state.mode !== nextState.mode));
    }

    componentDidUpdate = (prevProps, prevState) => {
        const nextView = this.props.viewStack.peek();

        const nextUid = nextView.uid;
        const [nextLayerType, nextLayerIndex] = nextView.layerSelection;

        if (this._uid !== nextUid ||
            this._layerType !== nextLayerType ||
            this._layerIndex !== nextLayerIndex) {

            this._uid = '';

            if (this.props.open) {
                this._uid = nextUid;
                this._layerType = nextLayerType;
                this._layerIndex = nextLayerIndex;
                this._queryData();
            }
        }
    }

    componentWillUnmount = () => {
        if (this._cancelToken) {
            this._cancelToken.cancel();
        }
    }

    render = () => {
        const view = this.props.viewStack.peek();

        if (!this.props.open) {
            return null;
        }

        if (this._pending) {
            return this._renderProgressSpinner();
        }

        this._uid = view.uid;

        switch (this.state.mode) {
            case Mode.progress:
                return this._renderProgressSpinner();
            case Mode.done:
                return this._renderGraphData();
            case Mode.error:
            default:
                return this._renderError();
        }
    }

    _renderProgressSpinner = () => {
        const { classes } = this.props;

        return (
            <div className={this.props.className}>
                <Paper
                    className={classes.paper}
                    square={false}
                    elevation={2}
                >
                    <CircularProgress
                        className={classes.progress}
                        size={60}
                    />
                </Paper>
            </div>
        );
    }

    _renderError = () => {
        const { classes } = this.props;

        return (
            <div className={this.props.className}>
                <Paper
                    className={classes.paper}
                    square={false}
                    elevation={20}
                >
                    <div className={classes.errorLabel}>
                        Fail to load graph data
                    </div>
                </Paper>
            </div>
        );
    }

    _renderGraphData = () => {
        const { classes } = this.props;

        return (
            <div className={this.props.className}>
                <Paper
                    className={classes.paper}
                    square={false}
                    elevation={2}
                >
                    <div className={classes.chartContainer}>
                        {(this.state.type === 'line') ?
                            (<Line
                                className={classes.chart}
                                data={this.state.data}
                                options={this.state.options}
                                redraw
                            />) : null
                        }
                        {(this.state.type === 'bar') ?
                            (<Bar
                                className={classes.chart}
                                data={this.state.data}
                                options={this.state.options}
                                redraw
                            />) : null
                        }
                        {(this.state.type === 'bar_stack') ?
                            (<Bar
                                className={classes.chart}
                                data={this.state.data}
                                options={this.state.options}
                                redraw
                            />) : null
                        }
                        {(this.state.type === 'pie') ?
                            (<Pie
                                className={classes.chart}
                                data={this.state.data}
                                options={this.state.options}
                                redraw
                            />) : null
                        }
                        {(this.state.type === 'donut') ?
                            (<Doughnut
                                className={classes.chart}
                                data={this.state.data}
                                options={this.state.options}
                                redraw
                            />) : null
                        }
                        {(this.state.type === 'polar') ?
                            (<Polar
                                className={classes.chart}
                                data={this.state.data}
                                options={this.state.options}
                                redraw
                            />) : null
                        }
                    </div>
                </Paper>
            </div>
        );
    }

    _signalError = () => {
        this.setState({
            data: null,
            scale: null,
            mode: Mode.error,
        });
    }

    _queryData = () => {
        const view = this.props.viewStack.peek();
        const selectedLayerData = view.selectedLayerData;

        if (!selectedLayerData || !selectedLayerData.graph) {
            this._signalError();
            return;
        }

        this.setState({
            mode: Mode.progress,
        });

        this._pending = true;

        this._requestData(view);
    }

    _chartOptions = (type) => {
        const { theme } = this.props;

        switch (type) {
            case 'common':
                return {
                    animation: false,
                    responsive: true,
                    maintainAspectRatio: false,
                    title: {
                        display: true,
                        fontSize: 16,
                        fontColor: theme.palette.primary.displayText,
                        padding: 0,
                    },
                    legend: {
                        display: false,
                        position: 'right',
                    },
                    layout: {
                        padding: {
                            top: 4,
                            bottom: 4,
                        }
                    },
                    tooltips: {
                        backgroundColor: theme.palette.primary.main,
                        borderColor: theme.palette.primary.lightBorder,
                        borderWidth: 1,
                        displayColors: false,
                        multiKeyBackground: theme.palette.primary.main,
                        titleFontColor: theme.palette.primary.displayText,
                        bodyFontColor: theme.palette.primary.displayText,
                        footerFontColor: theme.palette.primary.displayText,
                    },
                    scale: {
                        ticks: {
                            display: true,
                            showLabelBackdrop: false,
                            fontColor: theme.palette.primary.displayText,
                        },
                        gridLines: {
                            display: true,
                            color: fade(theme.palette.primary.contrastText, 0.2)
                        },
                    },
                    scales: {
                        xAxes: [{
                            id: 'first-x-axis',
                            type: 'category',
                            ticks: {
                                display: true,
                                autoSkip: false,
                                maxRotation: 0,
                                fontColor: theme.palette.primary.displayText,
                            },
                            gridLines: {
                                display: true,
                                drawTicks: true,
                                drawOnChartArea: false,
                                tickMarkLength: 5,
                                color: fade(theme.palette.primary.contrastText, 0.2)
                            },
                            scaleLabel: {
                                display: true,
                                labelString: '',
                                fontColor: fade(theme.palette.primary.contrastText, 0.67)
                            },
                        }],
                        yAxes: [{
                            id: 'first-y-axis',
                            type: 'linear',
                            ticks: {
                                suggestedMin: 0.0,
                                suggestedMax: 1.0,
                                display: true,
                                fontColor: theme.palette.primary.displayText,
                            },
                            gridLines: {
                                display: true,
                                drawTicks: true,
                                drawOnChartArea: false,
                                tickMarkLength: 5,
                                color: fade(theme.palette.primary.contrastText, 0.2)
                            },
                            scaleLabel: {
                                display: true,
                                labelString: '',
                                fontColor: fade(theme.palette.primary.contrastText, 0.67),
                            },
                        }]
                    }
                };
            case 'line':
                return {
                    fill: false,
                    backgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderWidth: 2,
                    pointRadius: 2,
                    pointHoverRadius: 3,
                    pointHitRadius: 6,
                    pointBackgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    pointBorderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    pointHoverBackgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    pointHoverBorderColor: emphasize(theme.palette.primary.contrastText, 0.1),
                };
            case 'bar':
                return {
                    backgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderWidth: 2,
                    hoverBackgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    hoverBorderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    hoverBorderWidth: 2,
                };
            case 'bar_stack':
                return {
                    backgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderWidth: 2,
                    hoverBackgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    hoverBorderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    hoverBorderWidth: 2,
                };
            case 'pie':
                return {
                    backgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderWidth: 2,
                    hoverBackgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    hoverBorderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    hoverBorderWidth: 2,
                };
            case 'polar':
                return {
                    backgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    borderWidth: 2,
                    hoverBackgroundColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    hoverBorderColor: emphasize(theme.palette.primary.contrastText, 0.27),
                    hoverBorderWidth: 2,
                };
            default:
                return {};
        }
    }

    _makeLineDataBins = (data, bin) => {
        if (!bin) {
            return;
        }

        const dataCount = data.labels.length;

        var max = null;

        for (const b of bin) {
            max = b.value;
            data.datasets.push({
                fill: (data.datasets.length === 1) ? 'origin' : '-1',
                data: this._makeLineDataSet(dataCount, b.value),
                showLine: true,
                borderColor: '#00000000',
                borderWidth: 1,
                backgroundColor: b.color,
                pointRadius: 0,
                pointHoverRadius: 0,
                pointHitRadius: 0,
            });
        }

        return max;
    }

    _makeLineDataSet = (length, value) => {
        var arr = new Array(length);

        for (var i = 0; i < length; ++i) {
            arr[i] = value;
        }

        return arr;
    }

    _makeIndexColorData = (data, bin) => {
        const color = [];

        for (const b of bin) {
            color.push(b.color);
        }

        data.datasets[0].backgroundColor = color;
        data.datasets[0].borderColor = color;
        data.datasets[0].hoverBackgroundColor = color;
        data.datasets[0].hoverBorderColor = color;
    }

    _makeGroupColorData = (data, bin) => {
        for (var i = 0; i < data.datasets.length; ++i) {
            if (!bin[i].color) {
                continue;
            }

            const color = bin[i].color;

            data.datasets[i].backgroundColor = color;
            data.datasets[i].borderColor = color;
            data.datasets[i].hoverBackgroundColor = color;
            data.datasets[i].hoverBorderColor = color;
        }
    }

    _shouldDisplayLabel = (datasets) => {
        for (const d of datasets) {
            if (d.label === undefined || d.label === '') {
                return false;
            }
        }

        return true;
    }

    _makeTooltipCallback = (type, binPara) => {
        switch (type) {
            default:
            case 'line':
            case 'bar':
                return {
                    title: (tooltipItemList, data) => {
                        const dataIndex = tooltipItemList[0].index;
                        const label = data.labels[dataIndex];
                        return label;
                    },
                    label: (tooltipItem, data) => {

                        const datasetIndex = tooltipItem.datasetIndex;
                        const dataIndex = tooltipItem.index;

                        if (data.customized_tooltip) {
                            this.value = data.customized_tooltip[datasetIndex].data[dataIndex];
                            this.label = data.customized_tooltip[datasetIndex].label;
                        } else {
                            this.value = data.datasets[datasetIndex].data[dataIndex];
                            this.label = data.datasets[datasetIndex].label;
                        }

                        if (this.label !== undefined || this.label !== '') {
                            return this.label + ': ' + this.value;
                        } else if (this.state.yAxisLabel) {
                            return this.state.yAxisLabel + ': ' + this.value;
                        } else {
                            return this.value;
                        }
                    },
                }
            case 'pie':
            case 'donut':
                return {
                };
            case 'polar':
                return {
                    title: (tooltipItemList, data) => {
                        const dataIndex = tooltipItemList[0].index;
                        const label = data.labels[dataIndex];

                        if (data.datasets.length <= 1) {
                            return label;
                        } else {
                            const datasetIndex = tooltipItemList[0].datasetIndex;
                            const subLabel = data.datasets[datasetIndex].label;

                            if (subLabel === undefined || subLabel === '') {
                                return label;
                            }

                            return label + ' (' + subLabel + ')';
                        }
                    },
                    label: (tooltipItem, data) => {
                        const datasetIndex = tooltipItem.datasetIndex;
                        const dataIndex = tooltipItem.index;

                        const value = data.datasets[datasetIndex].data[dataIndex];

                        if (this.state.yAxisLabel) {
                            return this.state.yAxisLabel + ': ' + value;
                        } else {
                            return value;
                        }
                    },
                };
            case 'bar_stack':
                return {
                    title: (tooltipItemList, data) => {
                        const dataIndex = tooltipItemList[0].index;
                        const label = data.labels[dataIndex];
                        return label;
                    },

                    labelColor: (tooltipItem) => {
                        const maxBinLength = binPara.length
                        const binIndex = maxBinLength - tooltipItem.datasetIndex - 1

                        if (binPara[binIndex].color.constructor === Array) {
                            return {
                                backgroundColor: binPara[binIndex].color[tooltipItem.index],
                                borderColor: binPara[binIndex].color[tooltipItem.index]
                            }
                        } else {
                            return {
                                backgroundColor: binPara[binIndex].color,
                                borderColor: binPara[binIndex].color
                            }
                        }
                    },

                    label: (tooltipItem, data) => {
                        const datasetIndex = tooltipItem.datasetIndex;
                        const dataIndex = tooltipItem.index;
                        const maxDataLength = data.datasets.length
                        const dataIndexReverse = maxDataLength - datasetIndex - 1

                        if (data.customized_tooltip) {
                            this.value = data.customized_tooltip[dataIndexReverse].data[dataIndex];
                            this.label = data.customized_tooltip[dataIndexReverse].label;

                        } else {
                            this.value = data.datasets[datasetIndex].data[dataIndex];
                            this.label = data.datasets[datasetIndex].label;
                        }

                        if (this.label !== undefined || this.label !== '') {
                            return this.label + ': ' + this.value;
                        } else if (this.state.yAxisLabel) {
                            return this.state.yAxisLabel + ': ' + this.value;
                        } else {
                            return this.value;
                        }
                    },
                }
        }
    }

    _requestData = async (view) => {
        try {
            const response = await http.get(api.map() + '/graph', {
                params: {
                    'access_code': this.props.getAccessCode(),
                    'organization': view.organization,
                    'region': view.key,
                    'layer_type': view.selectedLayerType,
                    'layer_key': view.selectedLayerData.originalIndexKey,
                },
                cancelToken: this._cancelToken.token,
            });

            this._pending = false;
            if (response.data.content.data && response.data.content.scale) {
                const type = (response.data.content.type) ? response.data.content.type : 'line';
                const scale = response.data.content.scale;
                const binPara = response.data.content.bin

                const options = this._chartOptions('common');
                Object.assign(options.scale.ticks, scale);
                Object.assign(options.scales.yAxes[0].ticks, scale);

                const data = Object.assign({}, response.data.content.data);

                for (var i = 0; i < data.datasets.length; ++i) {
                    Object.assign(data.datasets[i], this._chartOptions(type));
                }

                if (response.data.content.title !== undefined) {
                    options.title.text = [response.data.content.title, ''];
                    options.title.display = true;
                } else {
                    if (data.datasets.length === 1) {
                        options.title.text = [data.datasets[0].label, ''];
                        options.title.display = true;
                    } else {
                        options.title.display = false;
                    }
                }

                if (data.customized_tooltip) {
                    console.log("Cusotmized ToolTip Detected")

                }

                if (scale.xAxisLabel) {
                    options.scales.xAxes[0].scaleLabel.labelString = scale.xAxisLabel;
                }

                if (scale.yAxisLabel) {
                    options.scales.yAxes[0].scaleLabel.labelString = scale.yAxisLabel;
                }

                if (scale.xAxisOption) {
                    Object.assign(options.scales.xAxes[0], scale.xAxisOption);
                }

                if (scale.yAxisOption) {
                    Object.assign(options.scales.yAxes[0], scale.yAxisOption);
                }

                options.tooltips.callbacks = this._makeTooltipCallback(type, binPara);

                switch (type) {
                    default:
                    case 'line':
                        delete options.scale;
                        this._makeLineDataBins(data, response.data.content.bin);
                        break;
                    case 'bar':
                        delete options.scale;
                        this._makeIndexColorData(data, response.data.content.bin);
                        break;
                    case 'bar_stack':
                        delete options.scale;
                        options.tooltips.mode = 'index';
                        options.tooltips.displayColors = true;
                        options.scales.xAxes[0].stacked = true;
                        options.scales.yAxes[0].stacked = true;
                        options.legend.display = false;
                        this._makeGroupColorData(data, response.data.content.bin);
                        break;
                    case 'pie':
                    case 'donut':
                        delete options.scale;
                        delete options.scales;
                        options.legend.display = this._shouldDisplayLabel(data.datasets);
                        this._makeIndexColorData(data, response.data.content.bin);
                        break;
                    case 'polar':
                        delete options.scales;
                        options.legend.display = this._shouldDisplayLabel(data.datasets);
                        this._makeIndexColorData(data, response.data.content.bin);
                        break;
                }

                this.setState({
                    type: type,
                    data: data,
                    options: options,
                    xAxisLabel: scale.xAxisLabel,
                    yAxisLabel: scale.yAxisLabel,
                    mode: Mode.done,
                });
            } else {
                this._signalError();
            }
        } catch (error) {
            if (http.isCancel(error)) {
                return;
            }

            this._pending = false;
            this._signalError();
        }
    }
}

GraphPanel.propTypes = {
    getAccessCode: PropTypes.func.isRequired,
    viewStack: PropTypes.instanceOf(ViewStack).isRequired,
    open: PropTypes.bool.isRequired,
}

export default withStyles(styles, { withTheme: true })(GraphPanel);
